import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import NetworkSpinner from './components/ui/NetworkSpinner';
import { ThemeProvider } from './context/ThemeContext';
import { VocabularyProvider } from './context/VocabularyContext';
import { NotificationProvider } from './context/NotificationContext';
import { useNotificationToast }    from './hooks/useNotificationToast';
import { useDocumentTitle }         from './hooks/useDocumentTitle';
import { useBrowserNotifications }  from './hooks/useBrowserNotifications';
import ProtectedRoute from './components/ProtectedRoute';
import SubscriptionGuard from './components/SubscriptionGuard';
import ErrorBoundary from './components/ErrorBoundary';

// ── Layout (sincroni — necessari immediatamente) ──────────────────────────
import PublicLayout   from './components/PublicLayout';
import AppLayout      from './components/AppLayout';
import AdminLayout    from './components/AdminLayout';
import ManagerLayout  from './components/ManagerLayout';
import ImpersonationBanner from './components/ImpersonationBanner';
import BroadcastListener   from './components/ui/BroadcastListener';

// ── Fallback Suspense ─────────────────────────────────────────────────────
import PageSkeleton from './components/ui/PageSkeleton';

// ── Pagine pubbliche (lazy) ───────────────────────────────────────────────
const Login           = lazy(() => import('./pages/Login'));
const Register        = lazy(() => import('./pages/Register'));
const ForgotPassword  = lazy(() => import('./pages/ForgotPassword'));
const UpdatePassword  = lazy(() => import('./pages/UpdatePassword'));
const Onboarding      = lazy(() => import('./pages/Onboarding'));
const Paywall         = lazy(() => import('./pages/Paywall'));
const ConsentPage     = lazy(() => import('./pages/public/ConsentPage'));
const PrivacyPage     = lazy(() => import('./pages/public/PrivacyPage'));
const TermsPage       = lazy(() => import('./pages/public/TermsPage'));
const InviteExpired   = lazy(() => import('./pages/InviteExpired'));
const UnsubscribePage = lazy(() => import('./pages/UnsubscribePage'));

// ── Pagine app (lazy) ─────────────────────────────────────────────────────
const Dashboard         = lazy(() => import('./pages/app/Dashboard'));
const Clients           = lazy(() => import('./pages/app/Clients'));
const ClientDetail      = lazy(() => import('./pages/app/ClientDetail'));
const Items             = lazy(() => import('./pages/app/Items'));
const Suppliers         = lazy(() => import('./pages/app/Suppliers'));
const SupplierDetails   = lazy(() => import('./pages/app/SupplierDetails'));
const Profile           = lazy(() => import('./pages/app/Profile'));
const TeamManagement    = lazy(() => import('./pages/app/TeamManagement'));
const Settings          = lazy(() => import('./pages/app/Settings'));
const Comparatore       = lazy(() => import('./pages/app/Comparatore'));
const Changelog         = lazy(() => import('./pages/app/Changelog'));
const MarketingWeb      = lazy(() => import('./pages/app/MarketingWeb'));
const Compensi          = lazy(() => import('./pages/app/Compensi'));
const Championship      = lazy(() => import('./pages/app/Championship'));
const CensimentoPage    = lazy(() => import('./pages/app/CensimentoPage'));
const NotificationsPage = lazy(() => import('./pages/app/NotificationsPage'));
const AgencyCTE         = lazy(() => import('./pages/app/AgencyCTE'));

// ── Pagine admin (lazy) ───────────────────────────────────────────────────
const AdminOverview          = lazy(() => import('./pages/admin/AdminOverview'));
const AdminNetwork           = lazy(() => import('./pages/admin/AdminNetwork'));
const AdminPricing           = lazy(() => import('./pages/admin/AdminPricing'));
const AdminServices          = lazy(() => import('./pages/admin/AdminServices'));
const AdminImpersonate       = lazy(() => import('./pages/admin/AdminImpersonate'));
const AdminActivityLog       = lazy(() => import('./pages/admin/AdminActivityLog'));
const AdminBroadcast         = lazy(() => import('./pages/admin/AdminBroadcast'));
const AdminEmailTemplates    = lazy(() => import('./pages/admin/AdminEmailTemplates'));
const AdminChangelog         = lazy(() => import('./pages/admin/AdminChangelog'));
const AdminCensimentoModuli  = lazy(() => import('./pages/admin/AdminCensimentoModuli'));
const AdminChampionship      = lazy(() => import('./pages/admin/AdminChampionship'));
const AdminMRR               = lazy(() => import('./pages/admin/AdminMRR'));
const AdminDisputes          = lazy(() => import('./pages/admin/AdminDisputes'));

// ── Pagine manager (lazy) ─────────────────────────────────────────────────
const ManagerOverview       = lazy(() => import('./pages/manager/ManagerOverview'));
const ManagerNetwork        = lazy(() => import('./pages/manager/ManagerNetwork'));
const ManagerContracts      = lazy(() => import('./pages/manager/ManagerContracts'));
const ManagerCommunications = lazy(() => import('./pages/manager/ManagerCommunications'));
const ProspettoOfferte      = lazy(() => import('./pages/manager/ProspettoOfferte'));
const ManagerCTE            = lazy(() => import('./pages/manager/ManagerCTE'));

// ─────────────────────────────────────────────────────────────────────────
const BaseRedirect = () => {
  const { user, profile, loading } = useAuth();

  if (loading || (user && !profile)) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#030014' }}>
        <div className="flex flex-col items-center gap-3">
          <NetworkSpinner size={40} />
          <span className="text-violet-300/60 text-xs font-mono tracking-widest uppercase">Caricamento...</span>
        </div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  if (profile?.is_super_admin || profile?.role === 'admin') return <Navigate to="/admin/overview" replace />;
  if (profile?.role === 'manager') return <Navigate to="/manager/overview" replace />;
  return <Navigate to="/app/dashboard" replace />;
};

/** Attiva i side-effect globali delle notifiche (toast, titolo, OS) */
const GlobalNotifHooks = () => {
    useNotificationToast();
    useDocumentTitle();
    useBrowserNotifications();
    return null;
};

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <NotificationProvider>
          <VocabularyProvider>
            <GlobalNotifHooks />
            <Toaster position="top-right" richColors />
            <ImpersonationBanner />
            <BroadcastListener />

            {/* ErrorBoundary root — cattura qualsiasi crash non gestito */}
            <ErrorBoundary>
              <Suspense fallback={<PageSkeleton />}>
                <Routes>
                  {/*
                   * ── Pagine pubbliche ───────────────────────────────────────
                   */}
                  <Route element={<PublicLayout />}>
                    <Route path="/login"            element={<Login />} />
                    <Route path="/register"         element={<Register />} />
                    <Route path="/forgot-password"  element={<ForgotPassword />} />
                    <Route path="/update-password"  element={<UpdatePassword />} />
                    <Route path="/invite-expired"   element={<InviteExpired />} />
                    <Route path="/consent/:token"   element={<ConsentPage />} />
                    <Route path="/privacy"          element={<PrivacyPage />} />
                    <Route path="/terms"            element={<TermsPage />} />

                    <Route
                      path="/onboarding"
                      element={
                        <ProtectedRoute requireOrganization={false}>
                          <Onboarding />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/paywall"
                      element={
                        <ProtectedRoute requireOrganization={false}>
                          <Paywall />
                        </ProtectedRoute>
                      }
                    />
                  </Route>

                  {/*
                   * ── Admin routes (/admin/*) ────────────────────────────────
                   */}
                  <Route
                    path="/admin"
                    element={
                      <ProtectedRoute requireOrganization={false} requireAdmin>
                        <ErrorBoundary area="Admin">
                          <AdminLayout />
                        </ErrorBoundary>
                      </ProtectedRoute>
                    }
                  >
                    <Route path="overview"          element={<AdminOverview />} />
                    <Route path="managers"          element={<Navigate to="/admin/network" replace />} />
                    <Route path="network"           element={<AdminNetwork />} />
                    <Route path="pricing"           element={<AdminPricing />} />
                    <Route path="services"          element={<AdminServices />} />
                    <Route path="impersonate"       element={<AdminImpersonate />} />
                    <Route path="activity"          element={<AdminActivityLog />} />
                    <Route path="broadcast"         element={<AdminBroadcast />} />
                    <Route path="email-templates"   element={<AdminEmailTemplates />} />
                    <Route path="changelog"         element={<AdminChangelog />} />
                    <Route path="censimento-moduli" element={<AdminCensimentoModuli />} />
                    <Route path="championship"      element={<AdminChampionship />} />
                    <Route path="mrr"               element={<AdminMRR />} />
                    <Route path="disputes"          element={<AdminDisputes />} />
                    <Route index element={<Navigate to="overview" replace />} />
                  </Route>

                  {/*
                   * ── Manager routes (/manager/*) ───────────────────────────
                   */}
                  <Route
                    path="/manager"
                    element={
                      <ProtectedRoute requireOrganization={false} requireManager>
                        <ErrorBoundary area="Manager">
                          <ManagerLayout />
                        </ErrorBoundary>
                      </ProtectedRoute>
                    }
                  >
                    <Route path="overview"       element={<ManagerOverview />} />
                    <Route path="network"        element={<ManagerNetwork />} />
                    <Route path="contracts"      element={<ManagerContracts />} />
                    <Route path="prospetto"      element={<ProspettoOfferte />} />
                    <Route path="cte"            element={<ManagerCTE />} />
                    <Route path="communications" element={<ManagerCommunications />} />
                    <Route path="censimento"     element={<CensimentoPage />} />
                    <Route index element={<Navigate to="overview" replace />} />
                  </Route>

                  {/*
                   * ── App routes (auth + org required) ──────────────────────
                   */}
                  <Route
                    path="/app"
                    element={
                      <ProtectedRoute requireOrganization={true} requireAppAccess={true}>
                        <SubscriptionGuard>
                          <ErrorBoundary area="App">
                            <AppLayout />
                          </ErrorBoundary>
                        </SubscriptionGuard>
                      </ProtectedRoute>
                    }
                  >
                    <Route path="dashboard"    element={<Dashboard />} />
                    <Route path="clients"      element={<Clients />} />
                    <Route path="clients/:id"  element={<ClientDetail />} />
                    <Route path="items"        element={<Navigate to="/app/clients" replace />} />
                    <Route path="suppliers"    element={<Suppliers />} />
                    <Route path="suppliers/:id" element={<SupplierDetails />} />
                    <Route path="team"         element={<TeamManagement />} />
                    <Route path="profile"      element={<Profile />} />
                    <Route path="settings"     element={<Settings />} />
                    <Route path="comparatore"  element={<Comparatore />} />
                    <Route path="changelog"    element={<Changelog />} />
                    <Route path="marketing"    element={<MarketingWeb />} />
                    <Route path="compensi"     element={<Compensi />} />
                    <Route path="championship" element={<Championship />} />
                    <Route path="censimento"   element={<CensimentoPage />} />
                    <Route path="notifications" element={<NotificationsPage />} />
                    <Route path="cte-rete"      element={<AgencyCTE />} />
                  </Route>

                  <Route path="/unsubscribe" element={<UnsubscribePage />} />
                  <Route path="/"  element={<BaseRedirect />} />
                  <Route path="*"  element={<BaseRedirect />} />
                </Routes>
              </Suspense>
            </ErrorBoundary>

          </VocabularyProvider>
          </NotificationProvider>
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
